import React, { useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lightning, SignOut, FloppyDisk, CheckCircle, ArrowSquareOut, DotsSixVertical } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { usePlayer } from "../context/PlayerContext";
import { useAdminMode } from "../context/AdminModeContext";
import ModeSelector from "../components/admin/ModeSelector";
import OwnerModeIndicator from "../components/admin/OwnerModeIndicator";
import ModularLayout from "../components/admin/ModularLayout";
import TickerManager from "../components/admin/TickerManager";
import AnalyticsDashboard from "../components/admin/AnalyticsDashboard";
import SiteContentManager from "../components/admin/SiteContentManager";
import CommentsModeration from "../components/admin/CommentsModeration";
import BatchImportPanel from "../components/admin/BatchImportPanel";
import AdminInbox from "../components/admin/AdminInbox";
import ManagementEmails from "../components/admin/ManagementEmails";
import RatingDistribution from "../components/admin/RatingDistribution";
import SubmissionsManager from "../components/admin/SubmissionsManager";
import ArtistDirectory from "../components/admin/ArtistDirectory";
import AdminAccessList from "../components/admin/AdminAccessList";
import IsrcRegistry from "../components/admin/IsrcRegistry";
import { SlotMediaPanel, SlotTextFields, SlotMetaPanel } from "../components/admin/SlotEditorPanels";

function SlotEditor({ slot, onSaved, dragHandlers }) {
  const [songTitle, setSongTitle] = useState(slot.song_title || "");
  const [artistName, setArtistName] = useState(slot.artist_name || "");
  const [bio, setBio] = useState(slot.bio || "");
  const [videoUrl, setVideoUrl] = useState(slot.video_url || "");
  const [links, setLinks] = useState(() =>
    (slot.social_links || []).map((l, i) => ({ ...l, _key: `seed-${i}-${Date.now()}` }))
  );
  const [imagePath, setImagePath] = useState(slot.image_path || "");
  const [audioPath, setAudioPath] = useState(slot.audio_path || "");
  const [isrc, setIsrc] = useState(slot.isrc || "");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);

  const save = async () => {
    setSaving(true); setError("");
    try {
      const { data } = await api.put(`/slots/${slot.rank}`, {
        song_title: songTitle,
        artist_name: artistName,
        bio,
        video_url: videoUrl,
        social_links: links.filter((l) => l.platform && l.url).map(({ _key, ...rest }) => rest),
      });
      if (onSaved) onSaved(data);
      setSavedAt(Date.now());
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const upload = async (file, kind) => {
    if (!file) return;
    const setUploading = kind === "image" ? setUploadingImage : setUploadingAudio;
    setUploading(true); setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/slots/${slot.rank}/upload?kind=${kind}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (kind === "image") setImagePath(data.path);
      else {
        setAudioPath(data.path);
        if (data.isrc) setIsrc(data.isrc);
      }
      setSavedAt(Date.now());
      if (onSaved) onSaved({ ...slot, [kind === "image" ? "image_path" : "audio_path"]: data.path, ...(kind === "audio" && data.isrc ? { isrc: data.isrc } : {}) });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className={`rounded-lg border border-ink-600 bg-ink-800/40 overflow-hidden transition-all ${dragHandlers?.isDragging ? "opacity-50 scale-[0.99]" : ""} ${dragHandlers?.isOver ? "ring-2 ring-neon-cyan/60" : ""}`}
      data-testid={`admin-slot-${slot.rank}`}
      onDragOver={dragHandlers?.onDragOver}
      onDragLeave={dragHandlers?.onDragLeave}
      onDrop={dragHandlers?.onDrop}
    >
      <div className="flex items-center justify-between px-5 py-3 bg-ink-800/70 border-b border-ink-600">
        <div className="flex items-center gap-3 min-w-0">
          {dragHandlers && (
            <button
              draggable
              onDragStart={dragHandlers.onDragStart}
              onDragEnd={dragHandlers.onDragEnd}
              className="text-zinc-500 hover:text-neon-cyan cursor-grab active:cursor-grabbing -ml-1 p-0.5 rounded hover:bg-ink-700/60"
              title="Drag to reorder"
              data-testid={`admin-slot-${slot.rank}-drag-handle`}
              aria-label={`Drag handle for slot ${slot.rank}`}
            >
              <DotsSixVertical size={16} weight="bold" />
            </button>
          )}
          <span className="font-mono text-xs text-neon-cyan tracking-widest">#{String(slot.rank).padStart(2, "0")}</span>
          <span className="text-sm text-white font-medium truncate">{songTitle || "Untitled"}</span>
          <span className="text-xs text-zinc-500 truncate">— {artistName || "Unknown"}</span>
        </div>
        <div className="flex items-center gap-3">
          {savedAt && (
            <span className="text-xs text-neon-cyan inline-flex items-center gap-1 font-mono">
              <CheckCircle size={14} weight="fill" /> saved
            </span>
          )}
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-neon-cyan text-black text-xs font-semibold hover:bg-neon-cyanHover transition-colors disabled:opacity-60"
            data-testid={`admin-save-${slot.rank}`}
          >
            <FloppyDisk size={14} weight="bold" />
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="p-5 grid grid-cols-1 lg:grid-cols-12 gap-5">
        <SlotMediaPanel
          rank={slot.rank}
          imagePath={imagePath}
          uploading={uploadingImage}
          onUpload={(file) => upload(file, "image")}
        />
        <SlotTextFields
          rank={slot.rank}
          songTitle={songTitle} setSongTitle={setSongTitle}
          artistName={artistName} setArtistName={setArtistName}
          bio={bio} setBio={setBio}
        />
        <SlotMetaPanel
          rank={slot.rank}
          audioPath={audioPath}
          isrc={isrc}
          uploadingAudio={uploadingAudio}
          onAudioUpload={(file) => upload(file, "audio")}
          links={links}
          setLinks={setLinks}
          videoUrl={videoUrl}
          setVideoUrl={setVideoUrl}
        />
      </div>

      {error && <div className="px-5 pb-4 text-xs text-neon-magenta" data-testid={`admin-error-${slot.rank}`}>{error}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { setQueue } = usePlayer();
  const { isFull, isPresident } = useAdminMode();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/slots").then(({ data }) => {
      const list = data || [];
      setSlots(list);
      setLoading(false);
      // Autoplay after admin login too.
      setQueue(list, { autoplayFirst: true });
    }).catch(() => setLoading(false));
  }, [setQueue]);

  // Drag-to-reorder state. Track which slot is being dragged and which is hovered.
  const [dragRank, setDragRank] = useState(null);
  const [overRank, setOverRank] = useState(null);
  const reorderInflight = useRef(false);

  if (user === null) return <div className="min-h-screen flex items-center justify-center text-zinc-500" data-testid="admin-loading">Loading...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  const onSaved = (updated) => {
    setSlots(prev => prev.map(s => s.rank === updated.rank ? updated : s));
  };

  const commitReorder = async (fromRank, toRank) => {
    if (fromRank === toRank || reorderInflight.current) return;
    const current = [...slots].sort((a, b) => a.rank - b.rank);
    const fromIdx = current.findIndex((s) => s.rank === fromRank);
    const toIdx = current.findIndex((s) => s.rank === toRank);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...current];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    // Optimistic UI: temporarily renumber locally.
    const optimistic = next.map((s, i) => ({ ...s, rank: i + 1 }));
    setSlots(optimistic);
    reorderInflight.current = true;
    try {
      const order = next.map((s) => s.rank);
      await api.post("/admin/slots/reorder", { order });
      // Refresh to get authoritative order including vote stats.
      const { data } = await api.get("/slots");
      setSlots(data || []);
      setQueue(data || [], { autoplayFirst: false });
      toast.success(`Moved #${fromRank} → #${toRank}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      // Roll back: reload from server.
      const { data } = await api.get("/slots");
      setSlots(data || []);
    } finally {
      reorderInflight.current = false;
    }
  };

  const handlersFor = (rank) => ({
    isDragging: dragRank === rank,
    isOver: overRank === rank && dragRank !== rank,
    onDragStart: (e) => {
      setDragRank(rank);
      e.dataTransfer.effectAllowed = "move";
      try { e.dataTransfer.setData("text/plain", String(rank)); } catch (_err) { void _err; /* setData unsupported in some embedded browsers — DragEnd will still fire */ }
    },
    onDragEnd: () => { setDragRank(null); setOverRank(null); },
    onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dragRank && dragRank !== rank) setOverRank(rank); },
    onDragLeave: () => { if (overRank === rank) setOverRank(null); },
    onDrop: (e) => {
      e.preventDefault();
      const from = dragRank ?? parseInt(e.dataTransfer.getData("text/plain"), 10);
      setDragRank(null); setOverRank(null);
      if (Number.isFinite(from) && from !== rank) commitReorder(from, rank);
    },
  });

  return (
    <div className="min-h-screen bg-ink-900 text-white pb-24" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Lightning size={20} weight="fill" className="text-neon-cyan" />
            <span className="font-display font-black tracking-tight">UNDAGROUND SHOOW</span>
            <span className="text-xs font-mono text-zinc-500 tracking-widest uppercase ml-2 hidden sm:inline">Console</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-white inline-flex items-center gap-1" data-testid="admin-view-public">
              View public <ArrowSquareOut size={12} />
            </Link>
            <span className="hidden sm:inline text-xs text-zinc-500 font-mono">{user.email}</span>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-ink-600 text-zinc-300 hover:text-neon-magenta hover:border-neon-magenta/50 text-xs transition-colors"
              data-testid="admin-logout"
            >
              <SignOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-10">
          <p className="text-xs font-bold tracking-[0.3em] text-neon-cyan uppercase mb-3">Slot manager</p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">Edit the Top 10.</h1>
          <p className="text-sm text-zinc-500 mt-2">
            Swap artwork, audio, bios, social links. <span className="text-neon-cyan">Drag the handles</span> to reorder — all data (votes, plays, comments) follows the song.
            {!isFull && <span className="ml-1 text-zinc-400"> Analytics &amp; branding stay hidden until Full mode is unlocked in the lower-right.</span>}
          </p>
        </motion.div>

        {loading ? (
          <div className="text-zinc-500">Loading slots...</div>
        ) : (
          <div className="space-y-5" data-testid="admin-slot-list">
            {slots.map(slot => (
              <SlotEditor key={slot.rank} slot={slot} onSaved={onSaved} dragHandlers={handlersFor(slot.rank)} />
            ))}
          </div>
        )}

        <ModularLayout
          isUnlocked={(tier) => {
            if (tier === "agent") return true;
            if (tier === "full") return isFull;
            if (tier === "president") return isPresident;
            return false;
          }}
          sections={[
            { key: "artists", tier: "agent", label: "Artist Directory", render: () => <ArtistDirectory /> },
            { key: "batch-import", tier: "agent", label: "Batch Import", render: () => (
              <BatchImportPanel onApplied={() => {
                api.get("/slots").then(({ data }) => { setSlots(data || []); setQueue(data || [], { autoplayFirst: false }); });
              }} />
            )},
            { key: "submissions", tier: "agent", label: "Submissions", render: () => <SubmissionsManager /> },
            { key: "analytics", tier: "full", label: "Analytics", render: () => <AnalyticsDashboard /> },
            { key: "ratings", tier: "full", label: "Rating Distribution", render: () => <RatingDistribution /> },
            { key: "inbox", tier: "full", label: "Inbox", render: () => <AdminInbox /> },
            { key: "management-emails", tier: "full", label: "Management Emails", render: () => <ManagementEmails /> },
            { key: "isrc-registry", tier: "full", label: "ISRC Registry", render: () => <IsrcRegistry /> },
            { key: "comments", tier: "full", label: "Comments Moderation", render: () => <CommentsModeration /> },
            { key: "site-content", tier: "president", label: "Branding · Site Content", render: () => <SiteContentManager /> },
            { key: "ticker", tier: "president", label: "Branding · Banners", render: () => <TickerManager /> },
            { key: "admin-access", tier: "president", label: "Admin Access List", render: () => <AdminAccessList /> },
          ]}
        />
      </main>
      <ModeSelector />
      <OwnerModeIndicator />
    </div>
  );
}
